import React, { useState } from "react";
import { Alert, Box, CircularProgress, Container, Stack } from "@mui/material";
import { useOrganization } from "@clerk/react";

import { StudioCanvas } from "@/components/studio/StudioCanvas";
import { PagesPanel } from "@/components/studio/PagesPanel";
import { PageSettingsDialog } from "@/components/studio/PageSettingsDialog";
import { PublishScanDialog } from "@/components/studio/PublishScanDialog";
import { StudioTopBar, type StudioDevice, type StudioViewState } from "@/components/studio/StudioTopBar";
import { SiteStylesDrawer } from "@/components/editor/site-styles/SiteStylesDrawer";
import { DataDrawer } from "@/components/studio/DataDrawer";
import { PeopleDrawer } from "@/components/studio/PeopleDrawer";
import { SettingsDrawer } from "@/components/studio/SettingsDrawer";
import { HealthDrawer } from "@/components/studio/HealthDrawer";
import { TemplateGallery } from "@/components/templates/TemplateGallery";
import { OnboardingWizard } from "@/components/onboarding/OnboardingWizard";
import { usePageTitle } from "@/hooks/use-page-title";
import { useHasPermission } from "@/hooks/use-has-permission";
import { usePages } from "@/hooks/use-page-editor";
import { useIsPageEditor } from "@/hooks/use-page-editors";
import { useOrgProfile } from "@/hooks/use-org-profile";
import { useUnitDetails } from "@/hooks/use-unit-details";
import { useSite } from "@/hooks/use-site";
import { useSiteTemplates } from "@/hooks/use-site-templates";
import { usePublishScan, type PublishScanResult } from "@/hooks/use-publish-scan";
import { resolveCurrentPage } from "@/lib/page-tree";
import type { SiteTemplate } from "@/lib/templates/types";

export default function Studio(): React.JSX.Element {
  usePageTitle("Studio");
  const { isLoaded, organization } = useOrganization();
  const { site, loading: siteLoading, error: siteError } = useSite();
  const { profile } = useOrgProfile();
  const { unitDetails } = useUnitDetails();
  const { hasPermission: canManage, isLoading: canManageLoading } = useHasPermission("website_content", "manage");
  const { hasPermission: canManageSettings } = useHasPermission("website_settings", "manage");

  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [device, setDevice] = useState<StudioDevice>("desktop");
  const [viewState, setViewState] = useState<StudioViewState>("draft");
  const [themeSettingsOpen, setThemeSettingsOpen] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [pageSettingsId, setPageSettingsId] = useState<string | null>(null);
  // Set only while a publish preflight is in progress/showing for a
  // specific page — distinct from pageSettingsId so Page Settings can close
  // behind the scan dialog once a publish is requested.
  const [publishScanPageId, setPublishScanPageId] = useState<string | null>(null);
  const [publishScanResult, setPublishScanResult] = useState<PublishScanResult | null>(null);
  const { runScan, scanning: scanningForPublish, error: publishScanError } = usePublishScan();

  const {
    pages,
    loading: pagesLoading,
    error: pagesError,
    createPage,
    updatePage,
    deletePage,
    reorderPage,
    moveTopLevelPage,
    setHomePage,
  } = usePages(site?.id);
  const currentPage = pages.find((page) => page.id === selectedPageId) ?? resolveCurrentPage(pages, undefined);
  const pageSettingsPage = pages.find((page) => page.id === pageSettingsId) ?? null;
  const publishScanPage = pages.find((page) => page.id === publishScanPageId) ?? null;
  const { isPageEditor } = useIsPageEditor(currentPage?.id);
  // The org-wide website_content:manage permission gates everything; a
  // page-scoped editor additionally unlocks just the current page's canvas
  // (see page_editors RLS) — someone with neither sees a read-only canvas.
  const canManageCurrentPage = canManage || isPageEditor;
  const { applyTemplate, applying: applyingTemplate, error: templateError } = useSiteTemplates();

  const handleSelectTemplate = async (template: SiteTemplate) => {
    if (!site) return;
    await applyTemplate({
      siteId: site.id,
      template,
      unitName: organization?.name ?? "",
      unitIdentifier: unitDetails?.unit_number ?? "",
    });
  };

  // Publishing is page-by-page (each page's own Published switch in Page
  // Settings) — there is no site-level publish action. Turning a page's
  // switch on runs the youth-PII preflight first; the database triggers
  // remain the real backstop if this dialog is ever bypassed. The site
  // itself comes out of 'draft' automatically the moment any page
  // publishes (see the auto_publish_site_on_page_publish migration).
  const handleRequestPublish = (pageId: string) => {
    const page = pages.find((candidate) => candidate.id === pageId);
    if (!page) return;
    setPublishScanPageId(pageId);
    setPublishScanResult(null);
    void runScan(page).then(setPublishScanResult);
  };

  const handleConfirmPublish = () => {
    if (publishScanPageId) void updatePage(publishScanPageId, { is_published: true });
    setPublishScanPageId(null);
  };

  if (!isLoaded || siteLoading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!organization) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="warning">Select a unit to manage its website.</Alert>
      </Container>
    );
  }

  return (
    // 68px matches @scoutingops/ui's MainNav (its own --MainNav-height /
    // Toolbar minHeight) — the platform's shared top bar, rendered by this
    // app's own Layout above `children`, isn't reachable from here to read
    // that value directly since it's a sibling, not an ancestor, of this
    // page. Studio spans exactly the remaining viewport so the Pages
    // sidebar reaches from just below the platform nav to the bottom of
    // the screen, with its own internal scroll — not the whole page.
    <Box sx={{ display: "flex", height: "calc(100dvh - 68px)", overflow: "hidden" }}>
      <PagesPanel
        pages={pages}
        selectedPageId={currentPage?.id ?? null}
        canManage={canManage}
        onSelectPage={setSelectedPageId}
        onCreatePage={(title, parentPageId, showInNav) => {
          void createPage(title, parentPageId, showInNav).then((created) => setSelectedPageId(created.id));
        }}
        onOpenPageSettings={setPageSettingsId}
        onReorderPage={reorderPage}
        onMoveTopLevelPage={moveTopLevelPage}
        onOpenData={() => setDataOpen(true)}
        onOpenPeople={() => setPeopleOpen(true)}
        canManageSettings={canManageSettings && Boolean(site)}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenHealth={() => setHealthOpen(true)}
        canManageStyles={canManageSettings && Boolean(site)}
        onOpenStyles={() => setThemeSettingsOpen(true)}
      />

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <StudioTopBar
          pageTitle={currentPage?.title ?? organization.name}
          device={device}
          onDeviceChange={setDevice}
          viewState={viewState}
          onViewStateChange={setViewState}
        />

        {siteError ? <Alert severity="error" sx={{ mx: 3, mt: 2 }}>{siteError}</Alert> : null}
        {pagesError ? <Alert severity="error" sx={{ mx: 3, mt: 2 }}>{pagesError}</Alert> : null}
        {!canManageCurrentPage && !canManageLoading ? (
          <Alert severity="info" sx={{ mx: 3, mt: 2 }}>You have view-only access to this unit&apos;s website content.</Alert>
        ) : null}

        {/* No padding/max-width on desktop — the canvas fills the space
            exactly as the published page would, since draft and published
            are now the same inline view toggled by state, not a separate
            boxed-off editor mockup. Mobile stays capped to a phone-width
            column (it shouldn't stretch to fill the space) and centered. */}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <Box
            sx={{
              maxWidth: device === "mobile" ? 420 : "100%",
              mx: "auto",
              transition: (theme) => theme.transitions.create("max-width"),
            }}
          >
            {pages.length === 0 && !pagesLoading ? (
              !canManage ? (
                <Alert severity="info" sx={{ m: 3 }}>This site doesn&apos;t have any pages yet.</Alert>
              ) : !site || !profile ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !profile.onboarding_completed_at ? (
                <OnboardingWizard
                  site={site}
                  profile={profile}
                  unitName={organization.name}
                  onComplete={() => setSelectedPageId(null)}
                />
              ) : (
                <Stack spacing={2} sx={{ p: 3 }}>
                  {templateError ? <Alert severity="error">{templateError}</Alert> : null}
                  <TemplateGallery
                    unitType={profile.unit_type}
                    unitName={organization.name}
                    unitIdentifier={unitDetails?.unit_number ?? ""}
                    applying={applyingTemplate}
                    onSelectTemplate={(template) => void handleSelectTemplate(template)}
                  />
                </Stack>
              )
            ) : !currentPage ? (
              <Alert severity="info" sx={{ m: 3 }}>Add a page to get started.</Alert>
            ) : (
              <StudioCanvas
                pageId={currentPage.id}
                page={currentPage}
                site={site ?? null}
                canManage={canManageCurrentPage}
                viewState={viewState}
              />
            )}
          </Box>
        </Box>
      </Box>

      {site ? (
        <SiteStylesDrawer
          open={themeSettingsOpen}
          onClose={() => setThemeSettingsOpen(false)}
          site={site}
          unitName={organization.name}
        />
      ) : null}

      <DataDrawer open={dataOpen} onClose={() => setDataOpen(false)} pages={pages} />
      <PeopleDrawer open={peopleOpen} onClose={() => setPeopleOpen(false)} pages={pages} />
      {site ? <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} site={site} /> : null}
      {site ? <HealthDrawer open={healthOpen} onClose={() => setHealthOpen(false)} pages={pages} site={site} /> : null}

      <PageSettingsDialog
        open={pageSettingsPage !== null}
        onClose={() => setPageSettingsId(null)}
        page={pageSettingsPage}
        canManage={canManage}
        onUpdatePage={(updates) => {
          if (pageSettingsId) void updatePage(pageSettingsId, updates);
        }}
        onDeletePage={() => {
          if (!pageSettingsId) return;
          void deletePage(pageSettingsId);
          if (selectedPageId === pageSettingsId) setSelectedPageId(null);
          setPageSettingsId(null);
        }}
        onSetHomePage={() => {
          if (pageSettingsId) void setHomePage(pageSettingsId);
        }}
        onRequestPublish={() => {
          if (!pageSettingsId) return;
          setPageSettingsId(null);
          handleRequestPublish(pageSettingsId);
        }}
      />

      <PublishScanDialog
        open={publishScanPage !== null}
        pageTitle={publishScanPage?.title ?? ""}
        scanning={scanningForPublish}
        error={publishScanError}
        result={publishScanResult}
        onClose={() => setPublishScanPageId(null)}
        onConfirmPublish={handleConfirmPublish}
      />
    </Box>
  );
}
